// src/newDashboard/shared/PrintBarQr.jsx
//
// "Print QR" button for one bar (used on the admin pages).
// Makes the bar's real sign-up code the first time (the same code the owner
// sees later on their Today page, so sign-ups never split between two
// codes), then opens a print-ready sheet: bar name, big QR code, and the
// short code people can type in at sign-up.

import React, { useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { ensureBarCode, linkFor, qrFor, shortOf } from "./referrals";

function esc(text) {
  return String(text || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

// Wording on the printed card. Hotels (codes starting "hotel-") get
// wording for travelers; bars get "find tonight's fun here".
function wordingFor(code) {
  if (String(code).startsWith("hotel-")) {
    return { headline: "Staying nearby?", scan: "Scan to find tonight's bar specials, bingo &amp; live music near here" };
  }
  return { headline: "Find tonight's fun here!", scan: "Scan for tonight's specials, bingo &amp; events" };
}

function sheetHtml(barName, code, qr) {
  const w = wordingFor(code);
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(barName)} QR code</title>
<style>
  @page { size: letter; margin: 0.5in; }
  body { margin: 0; font-family: -apple-system, Helvetica, Arial, sans-serif; color: #111; text-align: center; }
  .sheet { padding: 24px; }
  .logo { width: 260px; margin: 0 auto 10px; display: block; }
  h1 { font-size: 40px; margin: 10px 0 6px; }
  .bar { font-size: 26px; color: #444; margin: 0 0 18px; }
  .qr { width: 380px; height: 380px; }
  .scan { font-size: 24px; font-weight: 700; margin: 14px 0 6px; }
  .code { font-size: 20px; color: #333; margin: 8px 0; }
  .code b { font-size: 34px; letter-spacing: 2px; }
  .small { font-size: 14px; color: #666; margin-top: 16px; }
  .btn { margin: 20px; padding: 12px 22px; font-size: 18px; border-radius: 10px; border: none; background: #FF3D81; color: #fff; cursor: pointer; }
  @media print { .btn { display: none; } }
</style></head><body><div class="sheet">
<button class="btn" onclick="window.print()">Print</button>
<img class="logo" src="https://barfunfinder.com/logo-full.png" alt="Bar Fun Finder">
<h1>${w.headline}</h1>
<p class="bar">${esc(barName)}</p>
<img class="qr" src="${qr}" alt="QR code">
<p class="scan">${w.scan}</p>
<p class="code">Signing up? Type code <b>${esc(shortOf(code))}</b></p>
<p class="small">${esc(linkFor(code))} · Free app · 21+</p>
</div></body></html>`;
}

/** Opens the print-ready card for any sign-up code (bars, hotels, flyers). */
export async function openCodeSheet(code, label) {
  const win = window.open("", "_blank");
  if (!win) throw new Error("Your browser blocked the pop-up. Allow pop-ups for this site and try again.");
  win.document.write("<p style='font-family:sans-serif;padding:20px'>Making the QR code…</p>");
  try {
    const qr = await qrFor(code, 800);
    win.document.open();
    win.document.write(sheetHtml(label || code, code, qr));
    win.document.close();
  } catch (e) {
    win.close();
    throw e;
  }
}

export default function PrintBarQr({ barId, barName, style }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const print = async () => {
    setMsg("");
    // Open the window right away (browsers block pop-ups opened later).
    const win = window.open("", "_blank");
    if (!win) {
      setMsg("Your browser blocked the pop-up. Allow pop-ups for this site and try again.");
      return;
    }
    win.document.write("<p style='font-family:sans-serif;padding:20px'>Making the QR code…</p>");
    setBusy(true);
    try {
      const snap = await getDoc(doc(db, "bars", barId));
      if (!snap.exists()) {
        win.close();
        setMsg("Save this bar first, then print its QR code.");
        setBusy(false);
        return;
      }
      const bar = { id: snap.id, ...snap.data() };
      const code = await ensureBarCode(bar);
      const qr = await qrFor(code, 800);
      win.document.open();
      win.document.write(sheetHtml(bar.name || barName, code, qr));
      win.document.close();
    } catch (e) {
      win.close();
      setMsg(`Couldn't make the QR code: ${e.message}`);
    }
    setBusy(false);
  };

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: "4px" }}>
      <button
        type="button"
        onClick={print}
        disabled={busy}
        style={{
          minHeight: "40px", padding: "0 14px", borderRadius: "10px", border: "none",
          background: "#FF3D81", color: "#0B0B12", fontWeight: 700, fontSize: "15px", cursor: "pointer",
          ...style,
        }}
      >
        {busy ? "Making…" : "Print QR"}
      </button>
      {msg && <span style={{ color: "#FFC2D1", fontSize: "13px", maxWidth: "260px" }}>{msg}</span>}
    </span>
  );
}
